<template>
    <div id="page-product-detail" class="page-products page-product-detail">
        <div class="page-title-box">
            <div class="page-title-right">
                <nav aria-label="breadcrumb">
                    <ol class="breadcrumb mb-0">
                        <li class="breadcrumb-item">
                            <router-link :to="{ name: 'home' }" href="#"
                                >Home</router-link
                            >
                        </li>
                        <li class="breadcrumb-item">
                            <router-link
                                :to="{
                                    name: 'website-products',
                                    params: { site_id: currentWebsiteId },
                                }"
                                href="#"
                                >Products</router-link
                            >
                        </li>
                        <li class="breadcrumb-item active" aria-current="page">
                            {{ productName }}
                        </li>
                    </ol>
                </nav>
            </div>

            <div class="page-title-left">
                <h2 class="page-title">Product Detail - {{ productName }}</h2>
            </div>
        </div>
        <div class="alert alert-success" role="alert" v-if="success">
            Product was saved.
        </div>
        <div class="split-view-container">
            <div class="section-model">
                <div class="card">
                    <div class="card-body">
                        <h4 class="header-title">Preview 3D Model</h4>
                        <div class="preview-image-container">
                            <model-viewer
                                id="sp-model-viewer"
                                :src="product.preview_model_url"
                                alt="Powered by seek-peek.com"
                                auto-rotate
                                camera-controls
                                style="width: 100%; height: 100%"
                            ></model-viewer>
                        </div>
                    </div>
                </div>
            </div>
            <div class="section-form">
                <div class="card">
                    <div class="card-body">
                        <h4 class="header-title mb-3">Product Page Settings</h4>
                        <div class="form-group row">
                            <label class="col-3 col-form-label">Name</label>
                            <div class="col-9">
                                <input
                                    type="text"
                                    class="form-control"
                                    v-model="form.name"
                                    :class="[hasErrorInputClass('name')]"
                                    :disabled="controlDisabled"
                                />
                                <form-error-label
                                    :target="formErrors['name']"
                                    error-class="invalid-feedback"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-3 col-form-label">State</label>
                            <div class="col-5 col-form-label">
                                <div
                                    class="custom-control custom-switch"
                                    v-if="false"
                                >
                                    <input
                                        type="checkbox"
                                        class="custom-control-input"
                                        id="option-state"
                                        v-model="form.state"
                                        :class="[hasErrorInputClass('state')]"
                                        :disabled="controlDisabled"
                                    />
                                    <label
                                        class="custom-control-label"
                                        for="option-state"
                                        >Active</label
                                    >
                                </div>
                                <custom-switch
                                    v-model="form.state"
                                    :class="[hasErrorInputClass('state')]"
                                ></custom-switch>
                                <form-error-label
                                    :target="formErrors['state']"
                                    error-class="invalid-feedback"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>
                        <div class="form-group row mb-3">
                            <label for class="col-3 col-form-label"
                                >Product URL</label
                            >
                            <div class="col-9">
                                <url-list
                                    v-model="form.url"
                                    :form-error-handler="formErrorHandler"
                                ></url-list>
                                <span class="help-block">
                                    <small
                                        >Enter the link of this product on your
                                        website</small
                                    >
                                </span>
                            </div>
                        </div>
                        <div class="form-group form-actions mb-0">
                            <button
                                @click="save"
                                class="btn btn-primary btn-save"
                                :disabled="controlDisabled"
                            >
                                Save
                            </button>
                            <button
                                @click="revert"
                                class="btn btn-secondary btn-revert"
                                :disabled="controlDisabled"
                            >
                                Revert
                            </button>

                            <span
                                class="spinner-border spinner-border-sm mr-1"
                                role="status"
                                aria-hidden="true"
                                v-if="loading || saving"
                            ></span>
                        </div>
                    </div>
                </div>
                <div
                    class="viewable-link-panel card"
                    v-if="product.viewable_link"
                >
                    <div class="card-body">
                        <h4 class="header-title mb-3">Viewable Link</h4>
                        <div class="row">
                            <div class="col-md-3">
                                <img
                                    class="qrcode"
                                    :src="product.viewable_link_qrcode"
                                />
                            </div>
                            <div class="col-md-9">
                                <p>
                                    You can view your product in AR or 3D
                                    through the link below:
                                </p>
                                <p>
                                    <a
                                        :href="product.viewable_link"
                                        target="_blank"
                                        v-text="product.viewable_link"
                                    ></a>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import { mapState } from "vuex";
import { defineComponent, ref } from "@vue/composition-api";

import product from "@/client/api/product";

import FormErrorLabel from "@/ui/FormErrorLabel";
import useFormErrorHandler from "@/utils/FormErrorHandler";

import CustomSwitch from "@/ui/CustomSwitch";

import UrlList from "@/admin/components/partials/UrlList.vue";

export default {
    components: {
        FormErrorLabel,
        CustomSwitch,
        UrlList,
    },
    data() {
        return {
            product: {},
            form: {},
            loading: false,
            saving: false,
            success: false,
        };
    },
    watch: {
        $route: {
            handler: "loadData",
            immediate: true,
        },
        currentWebsiteId(val, oldVal) {
            if (oldVal != null && val != oldVal) {
                this.$router.replace({
                    name: "website-products",
                    params: { site_id: val },
                });
            }
        },
    },
    setup() {
        let formErrorHandler = useFormErrorHandler();
        let {
            formErrors,
            formShowErrors,
            formErrorMessage,
            resetFormError,
            handleFormError,
            hasError,
            hasErrorInputClass,
        } = formErrorHandler;

        return {
            formErrorHandler: ref(formErrorHandler),
            formErrors,
            formErrorMessage,
            formShowErrors,
            resetFormError,
            handleFormError,
            hasError,
            hasErrorInputClass,
        };
    },
    methods: {
        async loadData() {
            this.loading = true;

            try {
                let res = await product.getProduct(
                    this.currentWebsiteId,
                    this.productId
                );
                if (res.data && res.data.data) {
                    this.product = res.data.data;
                }
            } catch (e) {
                console.error(e);
            }
            this.loading = false;
            this.revert();
        },
        revert() {
            this.form = {
                name: this.product.name,
                state: this.product.state,
                url: this.product.url,
            };
        },
        async save() {
            this.saving = true;
            this.success = false;
            this.resetFormError();
            try {
                let res = await product.putProduct(
                    this.currentWebsiteId,
                    this.productId,
                    this.form
                );

                if (res.data && res.data.status == "ok") {
                    this.loadData();

                    this.success = true;
                    setTimeout(() => {
                        this.success = false;
                    }, 5000);
                }
            } catch (e) {
                console.error(e);
                this.handleFormError(e);
            }
            this.saving = false;
        },
    },
    computed: {
        productId() {
            return this.$route.params.product_id;
        },
        productName() {
            return this.product.name;
        },
        controlDisabled() {
            return this.loading || this.saving;
        },
        ...mapState(["currentWebsiteId"]),
    },
    mounted() {
        let modelViewerModule = document.createElement("script");
        modelViewerModule.setAttribute("type", "module");
        modelViewerModule.setAttribute(
            "src",
            "https://unpkg.com/@google/model-viewer/dist/model-viewer.min.js"
        );
        document.head.appendChild(modelViewerModule);

        let modelViewerNoModule = document.createElement("script");
        modelViewerNoModule.setAttribute("nomodule", "");
        modelViewerNoModule.setAttribute(
            "src",
            "https://unpkg.com/@google/model-viewer/dist/model-viewer-legacy.js"
        );
        document.head.appendChild(modelViewerNoModule);
    },
};
</script>

<style lang="scss" scoped>
.split-view-container {
    display: grid;
    grid-template-columns: 1fr;
    gap: rem-calc(20);

    @include ipad {
        grid-template-columns: 3fr 2fr;
    }

    .section-model {
        order: 0;

        @include ipad {
            order: 1;
        }

        .preview-image-container {
            position: relative;
            padding-bottom: 100%;
            & > * {
                position: absolute;
                width: 100%;
                height: 100%;
                object-fit: cover;
                max-width: 100%;
            }
        }
    }

    .section-form {
        order: 1;

        @include ipad {
            order: 0;
        }
    }
}

.section-form {
    .photos-container {
        display: grid;
        grid-template-columns: repeat(auto-fill, rem-calc(80));
        grid-auto-rows: rem-calc(80);
        gap: rem-calc(20);

        & > .item {
            width: 100%;
            height: 100%;
            border-radius: rem-calc(5);
            overflow: hidden;
            padding: rem-calc(5);
            border: 1px solid #cccccc;

            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }

            .btn-add {
                background-color: transparent;
                width: 100%;
                height: 100%;
                border: 0;
                font-size: rem-calc(40);
                line-height: 0;
                color: $lighten-secondary-color;
                transition: color 0.3s ease;

                &:hover {
                    color: $secondary-color;
                }
            }
        }
    }
}

.viewable-link-panel {
    .qrcode {
        max-width: 100%;
    }
}
</style>